import React from 'react';

const Mission = () => {
  return (
    <section id="message" className="min-h-screen flex items-center py-20 px-4 relative">
      <div className="container mx-auto">
        <div className="max-w-6xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-16">
            <div className="text-6xl mb-6">💬</div>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading">
              My <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">Message</span> Is Simple
            </h2>
          </div>

          {/* Main Message */}
          <div className="mb-16 bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 border-4 border-primary/40 rounded-3xl p-12 md:p-16 text-center shadow-2xl">
            <h3 className="text-4xl md:text-6xl font-bold text-white mb-12 font-heading leading-tight">
              Break the Matrix
            </h3>
            
            <div className="space-y-8 text-xl md:text-2xl text-gray-200 leading-relaxed max-w-4xl mx-auto">
              <p className="flex items-start gap-4">
                <span className="text-primary text-3xl flex-shrink-0">→</span>
                <span>
                  <span className="text-accent font-bold">Question the rules</span>,<br className="hidden md:inline" />
                  not out of disrespect, but out of <span className="text-primary font-semibold">curiosity</span>
                </span>
              </p>
              
              <p className="flex items-start gap-4">
                <span className="text-secondary text-3xl flex-shrink-0">→</span>
                <span>
                  <span className="text-warning font-bold">Take decisions</span> for yourself,<br className="hidden md:inline" />
                  even if they're different from what others expect
                </span>
              </p>
              
              <p className="flex items-start gap-4">
                <span className="text-accent text-3xl flex-shrink-0">→</span>
                <span>
                  <span className="text-secondary font-bold">Explore the world</span>,<br className="hidden md:inline" />
                  beyond the boundaries set by fear
                </span>
              </p>
            </div>
          </div>

          {/* The Core Truth */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-danger/20 via-warning/20 to-danger/20 border-2 border-warning/50 rounded-3xl p-10 md:p-12 text-center">
              <p className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
                Until our minds are <span className="text-accent">free</span>
              </p>
              <p className="text-2xl md:text-4xl font-bold text-warning">
                No amount of progress will ever be real
              </p>
            </div>
          </div>

          {/* Action Steps */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-10 text-center font-heading">
              How To Start Breaking Free
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-primary/10 to-transparent border-2 border-primary/30 rounded-2xl p-6 hover:border-primary/60 transition-all duration-300 group">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">🧠</div>
                <h5 className="text-lg font-bold text-primary mb-3">Think Independently</h5>
                <p className="text-gray-400 text-sm">Question inherited beliefs, don't just accept</p>
              </div>

              <div className="bg-gradient-to-br from-accent/10 to-transparent border-2 border-accent/30 rounded-2xl p-6 hover:border-accent/60 transition-all duration-300 group">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">🎯</div>
                <h5 className="text-lg font-bold text-accent mb-3">Set Your Goals</h5>
                <p className="text-gray-400 text-sm">Define what YOU want, not what others want for you</p>
              </div>

              <div className="bg-gradient-to-br from-secondary/10 to-transparent border-2 border-secondary/30 rounded-2xl p-6 hover:border-secondary/60 transition-all duration-300 group">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">💪</div>
                <h5 className="text-lg font-bold text-secondary mb-3">Take Small Risks</h5>
                <p className="text-gray-400 text-sm">Start with small decisions, build confidence</p>
              </div>

              <div className="bg-gradient-to-br from-warning/10 to-transparent border-2 border-warning/30 rounded-2xl p-6 hover:border-warning/60 transition-all duration-300 group">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">🗣️</div>
                <h5 className="text-lg font-bold text-warning mb-3">Express Yourself</h5>
                <p className="text-gray-400 text-sm">Share your ideas, help others break free too</p>
              </div>
            </div>
          </div>

          {/* What This Means for Students */}
          <div className="mb-16 bg-gradient-to-br from-dark/80 to-darker/80 backdrop-blur-sm border-2 border-accent/30 rounded-3xl p-10 md:p-12">
            <h3 className="text-3xl font-bold text-accent mb-8 text-center font-heading">
              For Students & Young Professionals
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-lg text-gray-300">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-accent text-2xl">✓</span>
                  <span>Choose your field because <span className="text-white font-semibold">you're passionate</span>, not because parents said so</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-accent text-2xl">✓</span>
                  <span>It's okay to <span className="text-white font-semibold">change your mind</span> about your career path</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-accent text-2xl">✓</span>
                  <span><span className="text-white font-semibold">Failure in one attempt</span> doesn't define your life</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-accent text-2xl">✓</span>
                  <span>You can be <span className="text-white font-semibold">successful AND happy</span> on your own terms</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-accent text-2xl">✓</span>
                  <span>Building something is <span className="text-white font-semibold">not more risky</span> than a job</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-accent text-2xl">✓</span>
                  <span>Your worth is not determined by <span className="text-white font-semibold">what others think</span></span>
                </div>
              </div>
            </div>
          </div>

          {/* Final Call */}
          <div className="text-center">
            <div className="inline-block bg-gradient-to-br from-primary/20 via-accent/20 to-secondary/20 border-4 border-accent/50 rounded-3xl p-12 md:p-16 max-w-4xl shadow-2xl shadow-accent/20">
              <div className="text-6xl md:text-7xl mb-8">🔓</div>
              <h3 className="text-4xl md:text-5xl font-bold text-white mb-8 font-heading leading-tight">
                The Choice Is Yours
              </h3>
              <p className="text-2xl md:text-3xl text-gray-200 leading-relaxed mb-8">
                Continue living a life <span className="text-danger">designed by fear</span>,<br />
                or start building a life <span className="text-accent font-bold">driven by freedom</span>.
              </p>
              <div className="h-1 w-48 bg-gradient-to-r from-primary via-accent to-secondary mx-auto rounded-full mb-8"></div>
              <p className="text-xl text-gray-400 italic">
                Because your generation has the power to end this cycle — forever.
              </p>
            </div>
          </div>

          {/* Closing Note */}
          <div className="mt-16 text-center">
            <p className="text-lg text-gray-500 mb-4">
              This presentation is not about disrespecting our culture or parents.
            </p>
            <p className="text-xl text-white font-semibold">
              It's about <span className="text-primary">awareness</span>, <span className="text-accent">growth</span>, and <span className="text-secondary">freedom</span> —<br />
              for ourselves and future generations.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Mission;